import {User, userFromAPISchema} from '@gaia/types/User';
import fetchJSON from '@gaia/utils/fetchJSON';
import saveUserProfileSetting from '@gaia/utils/gaia/saveUserProfileSetting';
import {atom, getDefaultStore, useAtomValue, useSetAtom} from 'jotai';
import {loadable} from 'jotai/utils';
import {useCallback} from 'react';

export type UpdateUser = ReturnType<typeof useUpdateUser>;
export type RefreshUser = ReturnType<typeof useRefreshUser>;

// Jest export
export function fetchUser(): Promise<User> {
    return fetchJSON(
        '/api/user/v2/profile/',
        {
            headers: {
                'cache-control': 'no-store',
                pragma: 'no-cache',
            },
        },
        userFromAPISchema
    ).then((apiUser) => ({
        ...apiUser,

        // fix up some API inconsistencies
        coordinateFormat: apiUser.coordinateFormat || 'dd',
        unitSystem: apiUser.unitSystem || 'imperial',
        discoverAnalyticsEnabled: !!apiUser.discoverAnalyticsEnabled,
        canAccessNonPublicSources: !!apiUser.canAccessNonPublicSources,
        isRanger: !!apiUser.isRanger,
        isSU: !!apiUser.isSU,
    }));
}

//
//
////// Primitive Atoms

export const userPrimitiveAtom = atom<User | Promise<User>>(fetchUser()); // Jest export
const userOverridePrimitiveAtom = atom<Partial<User>>({});

//
//
////// Derived Atoms

const userAtom = atom(async (get) => {
    const user = await get(userPrimitiveAtom);
    const userOverride = get(userOverridePrimitiveAtom);
    return {...user, ...userOverride};
});

const loadableUserAtom = loadable(userAtom);

const updateUserAtom = atom(null, async (get, set, userUpdate: Partial<User>) => {
    const user = await get(userPrimitiveAtom);

    set(userPrimitiveAtom, {
        ...user,
        ...userUpdate,
    });

    if (user.isAuthenticated) {
        saveUserProfileSetting({
            email: user.email, // field required by API
            ...userUpdate,
        }).catch(console.error);
    }
});

//
//
////// Hooks

export function useUser() {
    return useAtomValue(userAtom);
}

/**
 * Exposing a {@link https://jotai.org/docs/utilities/async#loadable loadable atom} hook is
 * necessary for SSR pages until we can upgrade to React 18.
 * @returns loadable (SWR-like) object wrapping async user atom that doesn't suspend
 * @TODO refactor after React 18
 */
export function useLoadableUser() {
    return useAtomValue(loadableUserAtom);
}

export function useLoadableUserIsAuthenticated() {
    const userLoadingState = useLoadableUser();
    return userLoadingState.state === 'hasData' && userLoadingState.data.isAuthenticated;
}

/**
 * Updates the local `user` object with updated properties, and persists any
 * persistable properties (e.g., coordinate system) to the server for
 * authenticated users.
 * @returns a function that takes a partial user object and updates the local user
 */
export function useUpdateUser() {
    return useSetAtom(updateUserAtom);
}

/**
 * Update the local `user` with fresh data from the server. We don't want this to suspend,
 * so rather than calling `set(userPrimitiveAtom, fetchUser())`, we wait for `fetchUser`
 * first, and then set the result directly.
 * @returns a function that refreshes the `user` and returns a Promise that resolves when done
 */
export function useRefreshUser() {
    const setUser = useSetAtom(userPrimitiveAtom);
    return useCallback(() => {
        fetchUser().then(setUser, console.error);
    }, [setUser]);
}

//
//
////// Non-React

/**
 * Allow non-React code to subscribe to the `user`.
 * @param listener the callback to be called when the user is updated
 * @returns a function to unsubscribe the listener
 */
export function subscribe(listener: () => void) {
    getDefaultStore().sub(userAtom, listener);
}

/**
 * For code that either can't use a hook, or doesn't need re-render behavior
 * @returns Promise resolving with the current (overridden?) user object
 */
export function getUser(): Promise<User> {
    return getDefaultStore().get(userAtom);
}

/**
 * Override some properties of the `user` object so that calls to `useUser`
 * return a `user` object with those properties overridden. Also triggers
 * a re-render of all components that use `useUser`.
 * @param override the user properties to be overridden
 */
export function setUserOverride(userOverride: Partial<User>) {
    getDefaultStore().set(userOverridePrimitiveAtom, userOverride);
}
